import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import RichTextInput from 'components/RichTextInput';

import './styles/richText.scss';

const EMPTY_RICHTEXT_VALUE = { html: '', text: '' };

const propTypes = {
    autoFocus: PropTypes.bool,
    fieldId: PropTypes.string,
    hidden: PropTypes.bool,
    hint: PropTypes.string,
    onChange: PropTypes.func,
    readOnly: PropTypes.bool,
    shouldRenderField: PropTypes.func,
    value: PropTypes.shape({
        html: PropTypes.string,
        text: PropTypes.string,
    }),
};

const RichText = memo(
    ({
        autoFocus,
        fieldId,
        hidden,
        hint,
        onChange,
        readOnly,
        shouldRenderField,
        value,
        isEnriched = false,
    }) => {
        const resolvedValue = value ?? EMPTY_RICHTEXT_VALUE;
        const valueHtml = resolvedValue.html ?? '';
        const valueText = resolvedValue.text ?? '';
        const [innerValue, setInnerValue] = useState(resolvedValue);
        const prevHtmlRef = useRef(valueHtml);
        const prevTextRef = useRef(valueText);

        // ========================================
        // EFFECTS
        // ========================================
        useEffect(() => {
            if (prevHtmlRef.current === valueHtml && prevTextRef.current === valueText) {
                return;
            }

            prevHtmlRef.current = valueHtml;
            prevTextRef.current = valueText;
            setInnerValue({ html: valueHtml, text: valueText });
        }, [valueHtml, valueText]);

        // ========================================
        // EVENT HANDLERS & CALLBACKS
        // ========================================
        const handleChange = useCallback(
            (newValue) => {
                setInnerValue(newValue);
                onChange?.(newValue);
            },
            [onChange],
        );

        // ========================================
        // RENDER
        // ========================================
        if (hidden) return null;
        if (shouldRenderField && !shouldRenderField(fieldId)) return null;

        return (
            <div className="fm-field-richtext">
                <RichTextInput
                    autoFocus={autoFocus}
                    defaultValue={innerValue?.html || innerValue?.text || ''}
                    hint={hint}
                    isReadOnly={readOnly}
                    onChange={handleChange}
                    isEnriched={isEnriched}
                />
            </div>
        );
    },
);

RichText.displayName = 'RichText';
RichText.propTypes = propTypes;

export default RichText;
